import React, { useEffect, useState, useMemo } from "react";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  KeyboardArrowUpOutlined,
  KeyboardArrowDownOutlined,
} from "@mui/icons-material";
import { fetchProductData, createPreorder } from "../../api/productDataAPI";
import arrowLeftIcon from "../../assets/leftArrow.svg";
import editIcon from "../../assets/edit-icon.svg";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { addItem } from "../../redux/features/slices/cartSlice";
import { productEndPoints } from "../../constants/endpoints";
import { mapProduct } from "../../utils/productListMapping";
import Toast from "../../common/Toast";
import { useCart, useUpdateCart } from "../../queries/cart";
import { cartDataMapper } from "../../utils/cartDataMapper";
import CustomDropdown from "../../common/CustomDropdown";
import { sendCustomEvents } from "../../ga";
import ArrowLeft from "../../assets/arrow-left.svg";
import { ProductListingProps } from "../../types/ProductList";
import { useWishlist, useToggleWishlist } from "../../queries/wishlist"; // ✅ use wishlist hooks
import {
  addToWishlist,
  removeFromWishlist,
} from "../../redux/features/slices/wishlistSlice";
import { notifyWhenInStockAPI } from "../../api/notificationAPI";
import {
  ActionButton,
  ProductDetailsHeading,
  ActionsContainer,
  ArrowIcon,
  BackArrowIcon,
  ContentContainer,
  CustomDivider,
  DescriptionContainer,
  DescriptionText,
  DetailsSection,
  DropdownQuantityContainer,
  EditIconStyled,
  ImageSection,
  MainImage,
  MainImageContainer,
  OutOfStockText,
  PriceButtonContainer,
  PriceContainer,
  PriceText,
  PriceTextContainer,
  ProductDetailsContainer,
  ProductTitle,
  QuantityContainer,
  QuantityControlBox,
  QuantityIconButton,
  QuantityText,
  ReadMoreContainer,
  ReadMoreText,
  RightArrowIcon,
  ThumbnailButton,
  ThumbnailImage,
  ThumbnailsContainer,
  ThumbnailsImagesContainer,
  TitleEditContainer,
  TypeDropdownContainer,
  TypeLabel,
  EditContainer,
  AbvIbuInfo,
  CustomDropdownButtonStyles,
  FavoriteIconStyled,
  FavoriteBorderIconStyled,
} from "./ProductDetails.styles";
import PaymentDialog from "../../common/paymentDialog";
import SuccessIcon from "../../assets/images/GreenTick.png";

export const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [product, setProduct] = useState<any>(null);
  const [currentImage, setCurrentImage] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [count, setCount] = useState(1);
  const [value, setValue] = useState("20");
  const dispatch = useAppDispatch();
  const [error, setError] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [thumbnailPage, setThumbnailPage] = useState(0);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);

  const descriptionLimit = 300;
  const customerId = localStorage.getItem("customerId") || "";

  const { data: cartData } = useCart();
  const updateCart = useUpdateCart();

  const { data: wishlistQueryData } = useWishlist(customerId);
  const toggleWishlist = useToggleWishlist(customerId);

  const imagesPerPage = 3;
  const orgId = localStorage.getItem("orgId");

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        const foundProduct = await fetchProductData(
          id,
          productEndPoints.getProducts
        );
        const mappedData = mapProduct(foundProduct);
        setProduct(mappedData);
        if (mappedData?.mainImage) {
          setCurrentImage(mappedData.mainImage);
          const index1 = mappedData.images.findIndex(
            (img: string) => img === mappedData.mainImage
          );
          setCurrentImageIndex(index1 >= 0 ? index1 : 0);
        }
      } catch (error) {
        console.error("Error fetching product details:", error);
        setToastMessage("Something Went Wrong");
        setError(true);
        setShowToast(true);
      }
    };

    if (id) fetchProductDetails();
  }, [id]);

  useEffect(() => {
    if (!id || !cartData?.Products) return;
    const found = cartData.Products.find(
      (p: any) => String(p.productId) === String(id)
    );
    if (found) {
      setValue(String(found.quantity));
      setCount(+found.unit);
    }
  }, [id, cartData]);

  useEffect(() => {
    const products = wishlistQueryData?.products;
    if (Array.isArray(products)) {
      dispatch({ type: "wishlist/setWishlist", payload: products });
    }
  }, [wishlistQueryData, dispatch]);

  const handleImageClick = (imageUrl: string, index: number) => {
    setCurrentImageIndex(index);
    setCurrentImage(imageUrl);
  };
  const slideLeft = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const slideRight = () => {
    setCurrentImageIndex((prev) =>
      prev < productImages.length - 1 ? prev + 1 : prev
    );
  };

  const getTruncatedDescription = (text: string) => {
    if (showFullDescription || text.length <= descriptionLimit) return text;
    return `${text.slice(0, descriptionLimit)}...`;
  };

  const handleDecrement = () => setCount((prev) => Math.max(prev - 1, 1));
  const handleIncrement = () => setCount((prev) => prev + 1);
  const handleEdit = (id: string) =>
    navigate(`/maltora-seller/edit-product/${id}`);

  const handleChange = (selectedValue: string) => {
    // Check if the selected variant is out of stock
    let availableQuantity = 0;
    if (+selectedValue === 20)
      availableQuantity = product?.availableQuantity20L || 0;
    else if (+selectedValue === 30)
      availableQuantity = product?.availableQuantity30L || 0;
    else if (+selectedValue === 50)
      availableQuantity = product?.availableQuantity50L || 0;

    if (availableQuantity === 0) {
      setToastMessage(`${selectedValue}L variant is out of stock`);
      setError(true);
      setShowToast(true);
      return; // Don't change the value if variant is out of stock
    }

    setValue(selectedValue);
  };

  const handleAddToCart = async () => {
    if (product?.isOutOfStock) {
      setToastMessage(
        "This product is out of stock. We'll notify you when it's available."
      );
      setError(true);
      setShowToast(true);
      return;
    }

    let availableQuantity = 0;
    if (+value === 20) availableQuantity = product.availableQuantity20L || 0;
    else if (+value === 30)
      availableQuantity = product.availableQuantity30L || 0;
    else if (+value === 50)
      availableQuantity = product.availableQuantity50L || 0;

    if (count <= availableQuantity) {
      try {
        const updateCartData = {
          productId: product.id,
          unit: +count,
          quantity: +value,
        };

        const updatedCart = await updateCart.mutateAsync({
          cartData: [updateCartData],
        });
        const storeData = cartDataMapper(updatedCart?.Products);
        dispatch(addItem(storeData));
        setToastMessage("Product added to Cart successfully");
        setError(false);
        setShowToast(true);
        return true;
      } catch (error) {
        console.error("error adding to cart", error);
        setToastMessage("Something went wrong when updating the cart");
        setError(true);
        setShowToast(true);
      }
    } else {
      setToastMessage(`Product is out of stock for ${value}L`);
      setError(true);
      setShowToast(true);
      return;
    }
  };

  const handlePreOrder = async () => {
    const customerEmail = localStorage.getItem("userEmail") || "";

    try {
      const preorderData = {
        units: +count,
        quantity: +value,
        customerEmail: customerEmail,
      };

      const productId = product.id;

      await createPreorder(preorderData, productId);
      setIsPaymentDialogOpen(true);
      setToastMessage(
        "Pre-book successful! We’ll notify you when it's available."
      );
      setError(false);
      setShowToast(true);
    } catch (error: any) {
      console.error("Error in preorder:", error.message);
      setToastMessage("Something went wrong while placing preorder");
      setError(true);
      setShowToast(true);
    }
  };

  const handleNotifyMe = async () => {
    if (!product) {
      return;
    }

    const customerEmail = localStorage.getItem("userEmail") || "";

    try {
      const response = await notifyWhenInStockAPI({
        productId: product.id,
        productName: product.name,
        customerEmail: customerEmail,
      });

      if (response.success) {
        setToastMessage(response.message);
        setError(false);
        setShowToast(true);
      } else {
        setToastMessage(response.message);
        setError(true);
        setShowToast(true);
      }
    } catch (error: any) {
      setToastMessage("Something went wrong. Please try again later.");
      setError(true);
      setShowToast(true);
    }
  };

  const wishlist = useAppSelector((state) => state.wishlist.items);

  const handleLikeToggle = async (product: ProductListingProps) => {
    if (!product) return;
    const isLiked = wishlist.some(
      (item: ProductListingProps) => item.id === product.id
    );
    try {
      toggleWishlist.mutate(product.id);
      if (isLiked) {
        dispatch(removeFromWishlist(product.id));
      } else {
        dispatch(addToWishlist(product));
      }
    } catch (err) {
      console.error("Failed to update wishlist in backend", err);
    }
  };

  // Derived state
  const isSellerRoute = location.pathname.includes("/maltora-seller/products");
  const productImages = useMemo(() => product?.images || [], [product]);
  const startIndex = thumbnailPage * imagesPerPage;
  const visibleImages = productImages.slice(
    startIndex,
    startIndex + imagesPerPage
  );

  const getVariantOptions = useMemo(() => {
    return [
      {
        value: "20",
        label: "20 L",
        disabled: (product?.availableQuantity20L || 0) === 0,
      },
      {
        value: "30",
        label: "30 L",
        disabled: (product?.availableQuantity30L || 0) === 0,
      },
      {
        value: "50",
        label: "50 L",
        disabled: (product?.availableQuantity50L || 0) === 0,
      },
    ];
  }, [product]);

  const isItemOutOfStock = useMemo(() => {
    return product?.isPreorder ? "" : !isSellerRoute && product?.isOutOfStock;
  }, [product, isSellerRoute]);

  useEffect(() => {
    if (productImages[currentImageIndex]) {
      setCurrentImage(productImages[currentImageIndex]);
    }
  }, [currentImageIndex, productImages]);

  useEffect(() => {
    const newPage = Math.floor(currentImageIndex / imagesPerPage);
    if (newPage !== thumbnailPage) {
      setThumbnailPage(newPage);
    }
  }, [currentImageIndex, thumbnailPage, imagesPerPage]);

  const getPopupContent = () => {
    return (
      <>
        <Typography variant="body1" sx={{ fontStyle: "italic" }}>
          Checkout the <strong>Pre-Bookings</strong> tab for all the bookings
          made in advance.
        </Typography>
      </>
    );
  };

  return (
    <>
      {isSellerRoute && (
        <ProductDetailsHeading variant="h4">
          Product Details
        </ProductDetailsHeading>
      )}

      <ProductDetailsContainer>
        {isSellerRoute && (
          <EditContainer>
            <IconButton onClick={() => handleEdit(product?.id)}>
              <EditIconStyled src={editIcon} alt="Edit" />
            </IconButton>
          </EditContainer>
        )}

        <ContentContainer>
          {/* Image Section */}
          <ImageSection>
            <MainImageContainer>
              <MainImage
                src={currentImage || "image1.jpg"}
                alt="Product"
                isOutOfStock={
                  !product?.isPreorder &&
                  !isSellerRoute &&
                  product?.isOutOfStock
                }
                data-testid="main-image"
              />
            </MainImageContainer>

            {/* Thumbnails */}
            <ThumbnailsContainer>
              <ThumbnailButton
                onClick={slideLeft}
                disabled={currentImageIndex === 0}
                data-testid="prev-button"
              >
                <ArrowIcon src={arrowLeftIcon} alt="Previous" />
              </ThumbnailButton>

              <ThumbnailsImagesContainer data-testid="thumbnail-container">
                {visibleImages.map((image: string, index: number) => {
                  const index1 = startIndex + index;
                  return (
                    <ThumbnailImage
                      key={index}
                      src={image}
                      alt={`Thumbnail ${index1 + 1}`}
                      isActive={currentImageIndex === index1}
                      isOutOfStock={
                        !product?.isPreorder &&
                        !isSellerRoute &&
                        product?.isOutOfStock
                      }
                      onClick={() => {
                        handleImageClick(image, index1);
                      }}
                      data-testid={`thumbnail-image-${index}`}
                    />
                  );
                })}
              </ThumbnailsImagesContainer>

              <ThumbnailButton
                onClick={slideRight}
                disabled={currentImageIndex === productImages.length - 1}
                data-testid="next-button"
              >
                <RightArrowIcon src={arrowLeftIcon} alt="Next" />
              </ThumbnailButton>
            </ThumbnailsContainer>
          </ImageSection>

          {/* Details Section */}
          <DetailsSection>
            {/* Title and Edit */}
            {product && (
              <TitleEditContainer>
                {!isSellerRoute && (
                  <Box
                    onClick={() => {
                      if (isSellerRoute) {
                        navigate(`/maltora-seller/products`);
                      } else {
                        navigate(`/maltora-customer/products`);
                      }
                    }}
                    data-testid="back-button"
                  >
                    <BackArrowIcon src={ArrowLeft} alt="Back" />
                  </Box>
                )}

                <ProductTitle
                  isOutOfStock={
                    !product?.isPreorder &&
                    !isSellerRoute &&
                    product?.isOutOfStock
                  }
                  variant="h2"
                >
                  {product?.name}
                  {isSellerRoute && (
                    <>
                      <AbvIbuInfo>
                        {product?.abv ? `(${product.abv}% ABV)` : ""}
                        {product?.ibu ? ` IBU - ${product.ibu}` : ""}
                      </AbvIbuInfo>
                      <PriceText variant="h4">
                        Rs {product?.price.toLocaleString("en-IN")}
                      </PriceText>
                    </>
                  )}
                </ProductTitle>

                <Box>
                  {!isSellerRoute && !product?.isPreorder && (
                    <>
                      <IconButton
                        onClick={() => handleLikeToggle(product)}
                        data-testid="wishlist-icon"
                      >
                        {wishlist.some(
                          (w: ProductListingProps) => w.id === product.id
                        ) ? (
                          <FavoriteIconStyled />
                        ) : (
                          <FavoriteBorderIconStyled />
                        )}
                      </IconButton>
                    </>
                  )}
                </Box>
              </TitleEditContainer>
            )}
            {isSellerRoute && <CustomDivider />}
            <DescriptionContainer>
              <DescriptionText variant="body1">
                {getTruncatedDescription(product?.Description || "")}
              </DescriptionText>

              {product?.Description?.length > descriptionLimit && (
                <ReadMoreContainer>
                  <ReadMoreText
                    onClick={() => setShowFullDescription((prev) => !prev)}
                  >
                    {showFullDescription ? "Show less" : "Read more"}
                  </ReadMoreText>
                </ReadMoreContainer>
              )}
            </DescriptionContainer>
            {!isSellerRoute && <CustomDivider />}
            {/* Actions (Only for non-seller route) */}
            {!isSellerRoute && (
              <ActionsContainer>
                <DropdownQuantityContainer>
                  <TypeDropdownContainer>
                    <TypeLabel>Type</TypeLabel>
                    <Box sx={{ width: "100%" }}>
                      <CustomDropdown
                        label={`${value} L`}
                        value={value}
                        options={getVariantOptions}
                        onChange={handleChange}
                        customStyles={{
                          button: CustomDropdownButtonStyles,
                          labelClass: "custom-dropdown-label",
                        }}
                      />
                    </Box>
                  </TypeDropdownContainer>
                  {/* Quantity Control */}
                  <QuantityContainer>
                    <TypeLabel>Quantity</TypeLabel>
                    <QuantityControlBox>
                      <QuantityText>{count}</QuantityText>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <QuantityIconButton
                          onClick={handleIncrement}
                          aria-label="increase"
                        >
                          <KeyboardArrowUpOutlined />
                        </QuantityIconButton>
                        <QuantityIconButton
                          onClick={handleDecrement}
                          aria-label="decrease"
                          downButton
                        >
                          <KeyboardArrowDownOutlined />
                        </QuantityIconButton>
                      </Box>
                    </QuantityControlBox>
                  </QuantityContainer>
                </DropdownQuantityContainer>

                <Divider
                  sx={{
                    borderColor: "#979797 !important",
                    my: 5,
                  }}
                />
                {!isSellerRoute && (
                  <OutOfStockText variant="h2">
                    {isItemOutOfStock ? "Out of stock" : ""}
                  </OutOfStockText>
                )}
                <PriceContainer>
                  <PriceTextContainer>
                    <PriceText
                      variant="h2"
                      isOutOfStock={
                        !product?.isPreorder &&
                        !isSellerRoute &&
                        product?.isOutOfStock
                      }
                    >
                      Rs {product?.price.toLocaleString("en-IN")} /L
                    </PriceText>
                  </PriceTextContainer>
                  {/* Add to Cart */}
                  <PriceButtonContainer>
                    <ActionButton
                      data-testid="add-to-cart-button"
                      onClick={() => {
                        if (product?.isPreorder) {
                          sendCustomEvents(
                            "Customer",
                            "Clicked Preorder",
                            "Preorder in Product Description"
                          );
                          handlePreOrder();
                        } else if (product?.isOutOfStock) {
                          sendCustomEvents(
                            "Customer",
                            "Clicked Notify Me",
                            "Notify Me in Product Description"
                          );
                          handleNotifyMe();
                        } else {
                          sendCustomEvents(
                            "Customer",
                            "Clicked Add to Cart",
                            "Add to Cart in Product Description"
                          );
                          handleAddToCart();
                        }
                      }}
                    >
                      {product?.isPreorder
                        ? "Pre Book"
                        : product?.isOutOfStock
                          ? "Notify me"
                          : "Add to cart"}
                    </ActionButton>

                    {!product?.isPreorder && !product?.isOutOfStock && (
                      <ActionButton
                        onClick={async () => {
                          sendCustomEvents(
                            "Customer",
                            "Clicked Buy Now",
                            "Buy Now in Product Description"
                          );
                          const success = await handleAddToCart();
                          if (success) {
                            navigate(`/maltora-customer/cart`);
                          }
                        }}
                      >
                        Buy Now
                      </ActionButton>
                    )}
                  </PriceButtonContainer>
                </PriceContainer>
              </ActionsContainer>
            )}
          </DetailsSection>
        </ContentContainer>
      </ProductDetailsContainer>
      <PaymentDialog
        open={isPaymentDialogOpen}
        onClose={() => setIsPaymentDialogOpen(false)}
        title={
          <Typography variant="h6" fontWeight={500}>
            Pre-booking is confirmed for <strong>{product?.name}</strong> of
            quantity {+value * count}L.
          </Typography>
        }
        message={getPopupContent()}
        primaryText="Ok"
        primaryAction={() => {
          setIsPaymentDialogOpen(false);
          navigate(`/maltora-customer/home`);
        }}
        imageUrl={SuccessIcon}
      />
      {!isSellerRoute}
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
        data-testid={error ? "toast-error" : "toast-success"}
      />
    </>
  );
};
